import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Tooltip,
} from '@fluentui/react-components'
import { bundleIcon, DismissRegular, LinkFilled, LinkRegular } from '@fluentui/react-icons'
import useEntity from '../../../hooks/useEntity'
import { formatFullDate } from '../../../helpers/dateHelper'
import { InvoiceOverviewDto } from '../../../config/finance/invoiceConfig'
import { InvoiceListSettings } from '../../List/Invoice/InvoiceList'
import { formatInlineCurrency, parseNumber } from '../../../helpers/numberHelper'
import { Currency } from '../../../config/i18nConfig'
import MoneyText from '../../Text/Currency/MoneyText'
import { LegalAidCertificate } from './LegalAidCertificateForm'
import EntityCombobox from '../../Combobox/EntityCombobox'

type Props = {
    disabled: boolean
    fileId: string
    certificates: LegalAidCertificate[]
    currency: Currency
    targetValue: number
    tooltipContent: string
    onSubmit: (invoiceId: string) => void
}

const LinkIcon = bundleIcon(LinkFilled, LinkRegular)

function Body(props: Props): ReactElement {
    const existingInvoiceIds = useMemo(
        () =>
            props.certificates.flatMap(certificate =>
                [certificate.contributionInvoice?.id, certificate.settlementInvoice?.id].filter(Boolean),
            ),
        [props.certificates],
    )
    const [invoices] = useEntity<InvoiceOverviewDto[]>(
        () => ({
            url: '/finance/invoice',
            params: { files: [props.fileId] } as InvoiceListSettings,
        }),
        false,
        response =>
            response.data
                // Only allow invoices with the same currency as this certificate.
                .filter(invoice => invoice.price.currency === props.currency.alpha)
                // Only allow invoices that have not been used in another certificate.
                .filter(invoice => !existingInvoiceIds.includes(invoice.id))
                .sort((a, b) => b.date.localeCompare(a.date)),
    )
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>()
    const selectedInvoice = useMemo(
        () => invoices.data?.find(invoice => invoice.id === selectedInvoiceId),
        [selectedInvoiceId],
    )

    return (
        <DialogBody>
            <DialogTitle>Declaratie koppelen</DialogTitle>
            <DialogContent>
                <Field
                    label="Selecteer declaratie"
                    validationState="warning"
                    validationMessage={
                        selectedInvoice && parseNumber(selectedInvoice?.price.total, 'en') !== props.targetValue ? (
                            <>
                                Het totaalbedrag van deze declaratie wijkt af van de ingevulde waarde&nbsp;
                                <MoneyText
                                    inline
                                    currency={props.currency}
                                    amount={props.targetValue}
                                    textProps={{ size: 200 }}
                                />
                                &nbsp;(incl. BTW)
                            </>
                        ) : undefined
                    }
                >
                    <EntityCombobox<InvoiceOverviewDto>
                        dataPromise={invoices}
                        getEntityLabel={invoice =>
                            `${invoice.number} (${formatFullDate(invoice.date)} - ${formatInlineCurrency(
                                invoice.price.total,
                                props.currency,
                            )})`
                        }
                        getEntityValue={invoice => invoice.id}
                        selectedOptions={[selectedInvoiceId]}
                        onOptionSelect={(_, data) => setSelectedInvoiceId(data.optionValue)}
                    />
                </Field>
            </DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="transparent" icon={<DismissRegular />}>
                        Annuleren
                    </Button>
                </DialogTrigger>
                <DialogTrigger disableButtonEnhancement>
                    <Button
                        disabled={!selectedInvoiceId}
                        appearance="primary"
                        icon={<LinkRegular />}
                        onClick={() => props.onSubmit(selectedInvoiceId)}
                    >
                        Koppel
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}

export default function LinkInvoiceDialog(props: Props): ReactElement {
    return (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                <Tooltip content={props.tooltipContent} relationship="description" withArrow>
                    <Button icon={<LinkIcon />} appearance="subtle" size="large" disabled={props.disabled} />
                </Tooltip>
            </DialogTrigger>
            <DialogSurface>
                <Body {...props} />
            </DialogSurface>
        </Dialog>
    )
}
